import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Building2, Search } from 'lucide-react';
import { createLookup, resolveSearch } from '../../shared/api/endpoints';
import { ApiError, translateApiError } from '../../shared/api/client';
import type { IdentifierCandidateDto, ResolveResponse } from '@foco/contracts';

/**
 * The search screen.
 *
 * This is where the single field of section 5 does its work. The flow is shaped
 * by one rule: **a lookup is only charged when a card opens** (section 13.4), so
 * everything before that — resolving the type, disambiguating the country,
 * choosing from a list of matches — is free and is done here rather than after a
 * redirect.
 *
 * The resolver never guesses between countries. Eleven digits are a valid CPF, a
 * CUIT and a RUC at the same time; sending the wrong one to the wrong registry
 * produces a "not found" that reads as "this company does not exist", which is
 * the most damaging answer the product can give. When the checksums do not
 * decide, the user is asked.
 */
export function SearchPage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get('q') ?? '';

  const [query, setQuery] = useState(initial);
  const [resolution, setResolution] = useState<ResolveResponse | null>(null);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Resolve as the user types, debounced. Free, so there is no reason to make
  // them press a button twice.
  useEffect(() => {
    const trimmed = query.trim();
    setError(null);

    if (trimmed.length < 2) {
      setResolution(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setResolving(true);
      resolveSearch(trimmed, { signal: controller.signal })
        .then((result) => setResolution(result))
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          setResolution(null);
          if (cause instanceof ApiError && !cause.isNotFound) setError(translateApiError(cause, t));
        })
        .finally(() => {
          if (!controller.signal.aborted) setResolving(false);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const start = async (countryCode?: string, idType?: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setStarting(true);
    setError(null);

    try {
      const created = await createLookup({
        query: trimmed,
        countryCode,
        idType,
      });
      navigate(`/lookup/${created.lookupId}?q=${encodeURIComponent(trimmed)}`);
    } catch (cause) {
      setError(translateApiError(cause, t));
    } finally {
      setStarting(false);
    }
  };

  const candidates = resolution?.candidates ?? [];
  const verified = candidates.filter((candidate) => candidate.checksumValid);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="text-2xl font-bold">{t('search.label')}</h1>

      <form
        className="mt-5 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void start();
        }}
      >
        <div className="relative flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-fg)]"
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('search.placeholder')}
            aria-label={t('search.label')}
            autoFocus
            autoComplete="off"
            className="h-12 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] pl-9 pr-3 text-base outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
          />
        </div>
        <button
          type="submit"
          disabled={starting || query.trim().length < 2}
          className="h-12 shrink-0 rounded-[var(--radius-control)] bg-brand-700 px-5 font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {starting ? t('lookup.checking') : t('search.submit')}
        </button>
      </form>

      {query.trim().length > 0 && query.trim().length < 2 && (
        <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('search.minLength')}</p>
      )}
      {resolving && (
        <p className="mt-2 text-sm text-[var(--color-muted-fg)]" aria-live="polite">
          {t('search.resolving')}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-[var(--radius-card)] border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100"
        >
          <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* A bad check digit is a typo, not a company. Saying so is far more
          useful than a "not found" that implies the company does not exist. */}
      {resolution?.checksumMismatch && (
        <div className="mt-4 rounded-[var(--radius-card)] border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <p className="font-medium">{t('search.checksumMismatch')}</p>
          {candidates.length > 0 && (
            <p className="mt-1 text-xs opacity-90">
              {t('search.looksLike', { type: candidates[0].idType.toUpperCase() })}
            </p>
          )}
        </div>
      )}

      {/* Several countries accept the string: ask, never guess. */}
      {resolution?.needsCountryChoice && !resolution.checksumMismatch && (
        <div className="mt-4">
          <p className="text-sm font-medium">{t('search.needsCountry')}</p>
          <ul className="mt-2 space-y-1.5">
            {verified.map((candidate) => (
              <li key={`${candidate.countryCode}-${candidate.idType}`}>
                <button
                  type="button"
                  disabled={starting}
                  onClick={() => void start(candidate.countryCode, candidate.idType)}
                  className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] px-3 py-2 text-left text-sm hover:border-brand-600 disabled:opacity-60"
                >
                  <span className="font-medium">{candidate.countryCode}</span>{' '}
                  <span className="text-[var(--color-muted-fg)]">
                    {candidate.idType.toUpperCase()}
                  </span>{' '}
                  <span className="font-mono text-xs">{candidate.valueMasked}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* One unambiguous identifier: offer the check directly. */}
      {resolution?.kind === 'identifier' &&
        !resolution.needsCountryChoice &&
        verified.length > 0 &&
        !resolution.checksumMismatch && (
          <div className="mt-4 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3">
            <p className="text-sm">
              <span className="font-medium">
                {verified[0].idType.toUpperCase()} · {verified[0].countryCode}
              </span>{' '}
              <span className="font-mono text-xs text-[var(--color-muted-fg)]">
                {verified[0].valueMasked}
              </span>
            </p>
            <button
              type="button"
              disabled={starting}
              onClick={() => void start(verified[0].countryCode, verified[0].idType)}
              className="mt-2 rounded-[var(--radius-control)] bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
            >
              {starting ? t('lookup.checking') : t('search.submit')}
            </button>
          </div>
        )}

      {/* Free-text: show index matches so the user picks the right company. */}
      {resolution?.kind === 'name' && <SuggestionList resolution={resolution} onPick={start} busy={starting} />}

      {resolution?.kind === 'procurement' && (
        <p className="mt-4 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3 text-sm text-[var(--color-muted-fg)]">
          {t('search.procurementMode')}
        </p>
      )}
    </div>
  );
}

/** Company-name matches from the local index. */
function SuggestionList({
  resolution,
  onPick,
  busy,
}: {
  resolution: ResolveResponse & { suggestions?: unknown };
  onPick: (countryCode: string, idType: string) => void;
  busy: boolean;
}): React.ReactNode {
  const { t } = useTranslation();

  const suggestions = (resolution.suggestions ?? []) as {
    id: string;
    countryCode: string;
    idType: string;
    idValueMasked: string | null;
    name: string;
    status: string;
    city: string | null;
  }[];

  if (suggestions.length === 0) {
    return (
      <p className="mt-4 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3 text-sm text-[var(--color-muted-fg)]">
        {t('search.noCandidates')}
      </p>
    );
  }

  return (
    <div className="mt-4">
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-fg)]">
        {t('search.suggestions')}
      </p>
      <ul className="mt-2 divide-y divide-[var(--color-border)] rounded-[var(--radius-card)] border border-[var(--color-border)]">
        {suggestions.map((suggestion) => (
          <li key={suggestion.id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => onPick(suggestion.countryCode, suggestion.idType)}
              className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-[var(--color-surface-2)] disabled:opacity-60"
            >
              <Building2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-muted-fg)]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{suggestion.name}</span>
                <span className="mt-0.5 block text-xs text-[var(--color-muted-fg)]">
                  {suggestion.countryCode} · {suggestion.idType.toUpperCase()} ·{' '}
                  <span className="font-mono">{suggestion.idValueMasked ?? '—'}</span>
                  {suggestion.city && ` · ${suggestion.city}`}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export type { IdentifierCandidateDto };
